// Shared metadata for the four daily games, used by the cross-game
// KeepPlayingFooter (end screens) and GamesHistory (the /games hub). Keeping
// the labels, per-device localStorage session-key names, route builders, and
// identity colours in one place keeps those two surfaces in lockstep.

export type DailyGame = 'spot' | 'connections' | 'decode' | 'grid' | 'duel';

export const ALL_GAMES: DailyGame[] = [
  'duel',
  'spot',
  'connections',
  'decode',
  'grid',
];

export const GAME_META: Record<
  DailyGame,
  {
    label: string;
    /** compact label for tight UI like the day-nav row. */
    short: string;
    /** localStorage key holding this game's per-device session id. */
    sessionKey: string;
    /** Build the play/replay route for a given date. */
    href: (date: string) => string;
    /** Identity colour for chips/swatches. */
    color: string;
  }
> = {
  spot: {
    label: 'SPOT',
    short: 'SPOT',
    sessionKey: 'spot_session_key',
    href: (d) => `/spot/${d}`,
    color: '#1351d8',
  },
  connections: {
    label: 'Car Connections',
    short: 'Connections',
    sessionKey: 'cx_session_key',
    href: (d) => `/connections/${d}`,
    color: '#5aa469',
  },
  decode: {
    label: 'Decode',
    short: 'Decode',
    sessionKey: 'decode_session_key',
    href: (d) => `/decode/${d}`,
    color: '#d8a72b',
  },
  grid: {
    label: 'The Motoring Grid',
    short: 'Grid',
    sessionKey: 'grid_session_key',
    href: (d) => `/grid/${d}`,
    color: '#b56147',
  },
  duel: {
    label: 'Spec Duel',
    short: 'Duel',
    sessionKey: 'duel_session_key',
    href: (d) => `/duel/${d}`,
    color: '#c43b3b',
  },
};

export interface ProgressEntry {
  date: string;
  played: boolean;
  score: number | null;
  solved: boolean;
  /** Present only on played entries that carry one (Decode answer car). */
  label?: string;
  /** Present on entries whose puzzle carries one (SPOT's round photo). */
  image?: string;
}

export interface ProgressResponse {
  /** Every published date in range (completed + not), newest-first. */
  entries: ProgressEntry[];
  /** Finished-only subset of entries. */
  played: ProgressEntry[];
  nextUnplayed: string | null;
  latest: string | null;
}

/** Fetch one game's per-player progress through the same-origin game proxy. */
export async function fetchProgress(
  game: DailyGame,
  opts: { userId?: string; onOrBefore?: string },
): Promise<ProgressResponse | null> {
  try {
    const headers: Record<string, string> = {};
    const sk = window.localStorage.getItem(GAME_META[game].sessionKey);
    if (sk) headers['X-Session-Key'] = sk;
    if (opts.userId) headers['X-User-Id'] = opts.userId;
    const q = opts.onOrBefore ? `?onOrBefore=${opts.onOrBefore}` : '';
    const res = await fetch(`/api/spot/progress/${game}${q}`, { headers });
    if (!res.ok) return null;
    return (await res.json()) as ProgressResponse;
  } catch {
    return null;
  }
}

/** "Mon, Jun 15" — TZ-safe (noon anchor avoids date rollover). */
export function prettyDate(d: string): string {
  const dt = new Date(`${d}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return d;
  return dt.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

export interface SlateNeighbor {
  game: DailyGame;
  date: string;
}

/**
 * Given the union of every game's published dates (any order, possibly with the
 * current date included), find the nearest published day strictly before and
 * strictly after `date`. Because only published dates are passed in, the result
 * never points at a future or unpublished day, and neighbors naturally cross
 * game boundaries (each calendar date runs exactly one game). ISO YYYY-MM-DD
 * strings compare correctly lexicographically.
 */
export function computeSlateNeighbors(
  slate: SlateNeighbor[],
  date: string,
): { prev: SlateNeighbor | null; next: SlateNeighbor | null } {
  let prev: SlateNeighbor | null = null;
  let next: SlateNeighbor | null = null;
  for (const item of slate) {
    if (item.date < date) {
      if (!prev || item.date > prev.date) prev = item;
    } else if (item.date > date) {
      if (!next || item.date < next.date) next = item;
    }
  }
  return { prev, next };
}
